import { useState, useEffect, useRef } from 'react'
import type { CustomCellRendererProps } from 'ag-grid-react'
import { Input } from '@/components/ui/input'
import type { StoreModelMemberRead } from '@/types'

interface EditableOverrideCellParams {
  field: 'total_sales' | 'total_units'
  overrideField: 'sales_override' | 'units_override'
  formatValue: (value: number) => string
}

export function EditableOverrideCell(
  props: CustomCellRendererProps<StoreModelMemberRead>
) {
  const { field, overrideField, formatValue } = props.colDef
    ?.cellRendererParams as EditableOverrideCellParams

  const data = props.data
  const value = data?.[field] ?? null
  const isOverride = data?.[overrideField] ?? false
  const storeId = data?.store_id

  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editing])

  const handleStartEdit = () => {
    setEditValue(value != null ? String(value) : '')
    setEditing(true)
  }

  const handleSave = () => {
    setEditing(false)
    const numValue = editValue.trim() === '' ? null : Number(editValue)
    if (numValue !== value && storeId) {
      const context = props.context as {
        onOverride?: (
          storeId: string,
          field: 'total_sales' | 'total_units',
          value: number | null
        ) => void
      }
      context.onOverride?.(storeId, field, numValue)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setEditing(false)
    }
  }

  if (editing) {
    return (
      <Input
        ref={inputRef}
        type="number"
        step="any"
        value={editValue}
        onChange={e => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleSave}
        className="h-8 border-0 border-b-2 border-blue-500 rounded-none px-1 focus-visible:ring-0 focus-visible:ring-offset-0"
      />
    )
  }

  if (value == null) {
    return (
      <span
        className="text-sm text-muted-foreground cursor-pointer min-h-[2rem] flex items-center"
        onDoubleClick={handleStartEdit}
      >
        —
      </span>
    )
  }

  return (
    <span
      onDoubleClick={handleStartEdit}
      className={`text-sm text-foreground cursor-pointer min-h-[2rem] flex items-center px-1 ${
        isOverride ? 'bg-amber-50 border-l-2 border-amber-400' : ''
      }`}
    >
      {formatValue(value)}
      {isOverride && <span className="text-orange-500 ml-0.5">*</span>}
    </span>
  )
}
